/**
 * 新規動画の自動処理の設定（W25 / FR27.4・FR27.5）。動画メタの一覧の上に置く。
 *
 * この面が守っていること:
 * - **既定が「切」であることを言葉で書く。** 設定していない会社で最終検知が
 *   空なのは「0 本だった」ではなく「まだ自動では処理していない」である
 * - **押せるかどうかを画面が決めない。** `editableByViewer` はサーバーの
 *   `decide()` の答えそのもので、画面は役割を見て分岐しない
 * - **読んだ版をそのまま書き戻す。** `expectedVersion` は画面が数え直さず、
 *   問い合わせが返した `version` を渡す。食い違えば API が `CONFLICT` で断り、
 *   その断りをそのまま出す
 * - **上限で止まっていることを黙らない**（FR27.6）。帯に出す
 */

import { useState } from "react";
import { useMutation, useQuery } from "urql";
import {
  Button,
  Card,
  Tag,
  SetVideoMetaAutoIngestDocument,
  VideoMetaAutoIngestDocument,
} from "visumo-data-agent-web-shared";
import { HelpIcon } from "../help/HelpIcon";

import {
  VIDEO_AUTO_INGEST_FORBIDDEN_NOTE,
  VIDEO_AUTO_INGEST_NEVER_DETECTED_NOTE,
  VIDEO_AUTO_INGEST_NO_LIMIT_NOTE,
  VIDEO_AUTO_INGEST_NOTE,
  VIDEO_AUTO_INGEST_SUSPENDED_NOTE,
} from "../api-capabilities";
import { formatJst } from "../format-time";

/** 月の本数の上限の選択肢。**サーバーが受ける範囲の中だけ**を出す。 */
const LIMIT_CHOICES: readonly { value: string; label: string }[] = [
  { value: "none", label: "上限を置かない" },
  { value: "10", label: "月 10 本まで" },
  { value: "30", label: "月 30 本まで" },
  { value: "100", label: "月 100 本まで" },
];

function limitValue(monthlyLimit: number | null | undefined): string {
  if (monthlyLimit == null) return "none";
  return LIMIT_CHOICES.some((choice) => choice.value === String(monthlyLimit))
    ? String(monthlyLimit)
    : "none";
}

export function VideoMetaAutoIngestPanel() {
  const [result, refetch] = useQuery({
    query: VideoMetaAutoIngestDocument,
    requestPolicy: "cache-and-network" as const,
  });
  const [saveResult, setAutoIngest] = useMutation(
    SetVideoMetaAutoIngestDocument,
  );
  const [saving, setSaving] = useState(false);

  const setting = result.data?.videoMetaAutoIngest ?? null;
  const userErrors = saveResult.data?.setVideoMetaAutoIngest.userErrors ?? [];

  async function save(input: {
    enabled: boolean;
    monthlyLimit: number | null;
  }): Promise<void> {
    if (setting === null) return;
    setSaving(true);
    await setAutoIngest({
      input: {
        enabled: input.enabled,
        // null は「上限を置かない」。0 は送らない(サーバーが断る)
        ...(input.monthlyLimit === null
          ? {}
          : { monthlyLimit: input.monthlyLimit }),
        // 画面が数え直さず、読んだ版をそのまま渡す
        expectedVersion: setting.version,
      },
    });
    setSaving(false);
    // 断られたときも、いまの値を読み直してから出す
    refetch({ requestPolicy: "network-only" });
  }

  if (result.error !== undefined) {
    return (
      <Card
        title="新しい動画の自動処理"
        note={VIDEO_AUTO_INGEST_NOTE}
        extra={<HelpIcon slug="video-meta/auto-ingest" />}
      >
        <p className="vd-stat-note" style={{ margin: 0 }}>
          設定を読み込めませんでした。時間をおいて開き直してください。
        </p>
      </Card>
    );
  }

  if (setting === null) {
    return (
      <Card
        title="新しい動画の自動処理"
        note={VIDEO_AUTO_INGEST_NOTE}
        extra={<HelpIcon slug="video-meta/auto-ingest" />}
      >
        <p className="vd-stat-note" style={{ margin: 0 }}>
          設定を読み込んでいます。
        </p>
      </Card>
    );
  }

  const lastDetectedAt = formatJst(setting.lastDetectedAt);
  const busy = saving || saveResult.fetching;
  const canEdit = setting.editableByViewer;

  return (
    <Card
      title="新しい動画の自動処理"
      note={VIDEO_AUTO_INGEST_NOTE}
      extra={
        <span className="vd-row">
          <Tag tone={setting.enabled ? "ok" : "neutral"}>
            {setting.enabled ? "入" : "切"}
          </Tag>
          <HelpIcon slug="video-meta/auto-ingest" />
        </span>
      }
    >
      <div className="vd-stack">
        {setting.suspendedByLimit ? (
          <div className="vd-state vd-state--warn" role="status">
            <span className="vd-state-title">今月は上限に達しています</span>
            <span>{VIDEO_AUTO_INGEST_SUSPENDED_NOTE}</span>
          </div>
        ) : null}

        {canEdit ? null : (
          <p className="vd-stat-note" style={{ marginTop: 0 }}>
            {VIDEO_AUTO_INGEST_FORBIDDEN_NOTE}
          </p>
        )}

        {userErrors.length === 0 ? null : (
          <div className="vd-state vd-state--error" role="alert">
            <span className="vd-state-title">設定を変えられませんでした</span>
            {userErrors.map((error) => (
              <span key={`${error.code}-${error.message}`}>
                {error.message}
              </span>
            ))}
          </div>
        )}

        <div className="vd-row">
          <Button
            variant={setting.enabled ? "default" : "primary"}
            disabled={!canEdit || busy}
            onClick={() => {
              void save({
                enabled: !setting.enabled,
                monthlyLimit: setting.monthlyLimit ?? null,
              });
            }}
          >
            {busy
              ? "変えています"
              : setting.enabled
                ? "自動処理を止める"
                : "自動処理を始める"}
          </Button>

          <label>
            <span style={{ marginRight: 6 }}>月の本数</span>
            <select
              aria-label="月の本数"
              value={limitValue(setting.monthlyLimit)}
              disabled={!canEdit || busy}
              onChange={(event) => {
                const next = event.target.value;
                void save({
                  enabled: setting.enabled,
                  monthlyLimit: next === "none" ? null : Number(next),
                });
              }}
            >
              {LIMIT_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <p className="vd-stat-note" style={{ margin: 0 }}>
          {`今月これまでに自動で処理したのは ${String(setting.processedThisMonth)} 本です。`}
          {setting.monthlyLimit == null
            ? ` ${VIDEO_AUTO_INGEST_NO_LIMIT_NOTE}`
            : ` 上限は月 ${String(setting.monthlyLimit)} 本です。`}
        </p>

        <p className="vd-stat-note" style={{ margin: 0 }}>
          {lastDetectedAt === null
            ? VIDEO_AUTO_INGEST_NEVER_DETECTED_NOTE
            : `最後に新しい動画を見つけたのは ${lastDetectedAt} です。`}
        </p>
      </div>
    </Card>
  );
}
